import { Company, SubscriptionStatus } from '@/services/hsm-backend';

/**
 * Status shown on the "RECIENTES" chip. Beyond the real subscription states it
 * adds `'migration-pending'` — a client-side-only flag set when activation
 * fails with LEGACY_SUBSCRIPTION_ACTIVE (no GET-time signal exists for it).
 * `null` means the company has no HSM subscription.
 */
export type RecentSubscriptionStatus =
  | SubscriptionStatus
  | 'migration-pending'
  | null;

export interface RecentCompany {
  companyId: string;
  name: string;
  subscriptionStatus: RecentSubscriptionStatus;
}

export const STORAGE_KEY = 'new-hsm-admin:recent-companies';
const MAX_ITEMS = 5;

export function getRecent(): RecentCompany[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isRecentCompany).slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

export function rememberCompany(company: Company): void {
  const incoming = company.subscription?.status ?? null;
  const previous = getRecent().find((c) => c.companyId === company.companyId);
  // Preserve a known "migration-pending" flag across re-visits: GET /company
  // keeps returning no subscription for legacy companies, so a plain reload
  // would otherwise wipe the orange chip back to "Sin suscripción". A real
  // subscription (active/inactive) still overrides it.
  const subscriptionStatus: RecentSubscriptionStatus =
    incoming === null && previous?.subscriptionStatus === 'migration-pending'
      ? 'migration-pending'
      : incoming;

  const next: RecentCompany = {
    companyId: company.companyId,
    name: company.name,
    subscriptionStatus,
  };

  const current = getRecent().filter((c) => c.companyId !== next.companyId);
  const updated = [next, ...current].slice(0, MAX_ITEMS);

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // localStorage no disponible (modo privado, quota) — silenciamos.
  }
}

/**
 * Flags an already-remembered company as "migration-pending" (legacy
 * subscription active in TSA). Called from the activation flow when the POST
 * returns LEGACY_SUBSCRIPTION_ACTIVE; there's no GET-time signal, so we persist
 * what the attempt revealed. No-op if the company isn't in the recent list.
 */
export function markMigrationPending(companyId: string): void {
  const current = getRecent();

  if (!current.some((c) => c.companyId === companyId)) {
    return;
  }
  const updated = current.map((c) =>
    c.companyId === companyId
      ? { ...c, subscriptionStatus: 'migration-pending' as const }
      : c
  );

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // localStorage no disponible — silenciamos.
  }
}

export function clearRecent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

function isRecentCompany(value: unknown): value is RecentCompany {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;

  return typeof v.companyId === 'string' && typeof v.name === 'string';
}
